import classNames from "classnames";
import { RichText } from "components/RichTextWidget";
import { fetchAPI } from "features/cms";
import { useTranslation } from "features/translations";
import React, { useEffect, useState } from "react";
import {
  Action,
  Heading4,
  Heading5,
  Icon,
  PageLink,
  Paragraph,
  RenderIf,
} from "shared/components";
import { useViewMode, VIEW_MODES } from "shared/contexts/ViewMode";

import { useWidgetContent } from "../Content064.context";
import { CourseSkeleton } from "../CourseSkeleton";
import styles from "./CourseInfo.module.scss";

export const MobileLabel = ({ label }) => {
  const { t } = useTranslation();

  return (
    <RenderIf viewMode={VIEW_MODES.MOBILE}>
      <Paragraph className={styles.subtitle} color="dark">
        {t(label)}
      </Paragraph>
    </RenderIf>
  );
};

export const HeadingsBlock = ({ metrics, dataKey }) => {
  const { t } = useTranslation();
  let text = metrics[dataKey];

  if (typeof metrics[dataKey] === "boolean") {
    text = metrics[dataKey] ? t("yes") : t("no");
  }

  return (
    <Heading5
      className={classNames({
        [styles.empty]: !Boolean(metrics[dataKey]),
      })}
    >
      {text}
    </Heading5>
  );
};

export const RichTextBlock = ({ metrics, dataKey }) => {
  return (
    <RichText
      value={metrics?.[dataKey]}
      className={classNames({
        [styles.empty]: !Boolean(metrics?.[dataKey]),
      })}
    />
  );
};

export const ProgramPrice = ({ metrics, dataKey }) => {
  return (
    <>
      {metrics[dataKey]?.map(({ id, price, description, caption }) => {
        return (
          <div key={id} className={styles["program-price__block"]}>
            <Heading5 className={styles["program-price__title"]}>
              {price}
            </Heading5>
            <Paragraph color="dark" appearance="smaller">
              {description}
            </Paragraph>
            <Paragraph color="grey" appearance="smaller">
              {caption}
            </Paragraph>
          </div>
        );
      }) ?? "-"}
    </>
  );
};

export const ButtonView = () => {
  const { isDesktop } = useViewMode();
  const { t } = useTranslation();

  return isDesktop ? t("more_about_program") : t("more");
};

export const METRICS_MAPPING = {
  suitsTo: RichTextBlock,
  duration: HeadingsBlock,
  programPrice: ProgramPrice,
  advantages: RichTextBlock,
  internationalModule: HeadingsBlock,
  results: RichTextBlock,
  diploma: RichTextBlock,
};

export const CourseInfo = ({ slug }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(true);
  const [data, setData] = useState(null);
  const { locale, onDeleteCourse, availableMetrics } = useWidgetContent();
  const { isDesktop } = useViewMode();

  useEffect(() => {
    fetchAPI(
      `/courses`,
      {
        populate: {
          metrics: {
            populate: "*",
          },
        },
        filters: {
          slug: {
            $eq: slug,
          },
        },
      },
      locale
    )
      .then((res) => {
        setData(res?.data[0]);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setPending(null);
      });
  }, [locale, slug]);

  if (pending) {
    return <CourseSkeleton />;
  }

  return (
    <>
      <div className={styles.top}>
        <div className={styles.heading}>
          {isDesktop && (
            <Paragraph
              color="grey"
              className={styles.label}
              appearance="smaller"
            >
              {t("selected_course")}
            </Paragraph>
          )}

          <Heading4 className={styles.title} color="light">
            {error ? t("undefined_course") : data.title}
          </Heading4>
        </div>
        {isDesktop && (
          <Action className={styles.button} onClick={onDeleteCourse(slug)}>
            <Icon name="close_white" />
          </Action>
        )}
      </div>
      {availableMetrics?.map((metrics) => {
        const Block = METRICS_MAPPING[metrics.dataKey];
        const styleKey = metrics.dataKey
          .replace(/([A-Z])/g, "-$1")
          .toLowerCase();
        return (
          <React.Fragment key={metrics.title}>
            <MobileLabel label={metrics.title} />
            <div className={styles[metrics.template]}>
              <div className={styles[styleKey]}>
                <Block metrics={data.metrics} dataKey={metrics.dataKey} />
              </div>
            </div>
          </React.Fragment>
        );
      })}

      <PageLink directory="programs" slug={slug} className={styles.link}>
        <RenderIf viewMode={VIEW_MODES.MOBILE}></RenderIf>
        <Paragraph
          color="dark"
          appearance="bold"
          uppercase
          className={styles.more}
        >
          <ButtonView />
        </Paragraph>
      </PageLink>
    </>
  );
};
